import type { Category, MenuItem } from "@/lib/api/types";
import type { DoodleBadgeId } from "./badges.data";
import { isDoodleBadgeId } from "./library";

/**
 * A menu SECTION's stored Doodles badge (`Category.badge`, founder 2026-10-03:
 * "categories take an icon name"), resolved through the library.
 *
 * `null` when the section has none — or holds an id this build of the library
 * does not draw, which is the same thing on screen: every caller then draws
 * the mark it drew before the column existed (the till's name-derived badge,
 * the menu's plate, the emoji). The server checks only the id's shape, so this
 * is the one place an unknown id is caught.
 */
export function storedCategoryBadge(
  category: Pick<Category, "badge"> | null | undefined,
): DoodleBadgeId | null {
  const value = category?.badge;
  return isDoodleBadgeId(value) ? value : null;
}

/**
 * The badge a dish WITHOUT a photo falls back to: its section's stored badge.
 *
 * "Its section" is the first of `sections` the dish is filed under — the same
 * section the menu table names in its Category column (`primarySection`) and
 * the till's dish tile takes, so a dish never wears one section's tag and
 * another's badge. `null` when that section has no badge (the caller keeps its
 * own fallback) or the dish is in no section at all.
 */
export function dishSectionBadge(
  item: Pick<MenuItem, "categoryIds">,
  sections: readonly Category[],
): DoodleBadgeId | null {
  const section = sections.find((row) => item.categoryIds.includes(row.id));
  return storedCategoryBadge(section);
}

/**
 * A DISH's own stored Doodles badge (`MenuItem.badge`, founder 2026-10-03:
 * the dish form offers "a badge or a photo"), resolved through the library.
 * `null` when it has none, or an id this build does not draw.
 */
export function storedDishBadge(
  item: Pick<MenuItem, "badge"> | null | undefined,
): DoodleBadgeId | null {
  const value = item?.badge;
  return isDoodleBadgeId(value) ? value : null;
}

/**
 * The badge a dish WITHOUT a photo is drawn with: the one picked for the dish
 * itself, else its section's (`dishSectionBadge`), else `null` — the caller's
 * own default (the plate). Every photo-less dish on every PAPER surface asks
 * this, so a dish wears one badge everywhere.
 */
export function dishBadge(
  item: Pick<MenuItem, "badge" | "categoryIds">,
  sections: readonly Category[],
): DoodleBadgeId | null {
  return storedDishBadge(item) ?? dishSectionBadge(item, sections);
}
